import { useState, useCallback, useRef } from "react";
import useInspectionAttributes from "../../../hooks/query/useInspectionAttributes";
import { isEmptyMeasurementAttributeValue } from "../../../utils/measurementAttribute";
import { BackendAttribute } from "./useInit";
import { expandMeasurementsForSync } from "../utils/measurements";
import { useNetworkStatus } from "../../../hooks/useNetworkStatus";
import { offlineStore } from "../../../utils/offlineStore";

type SyncParams = {
    baselineAttributes: BackendAttribute[];
    setBaselineAttributes: React.Dispatch<
        React.SetStateAction<BackendAttribute[]>
    >;
    currentData: Record<string, any>;
    projectId: string;
};

type CreateOperation = {
    ProjectId: string;
    AttributeName: string;
    Value: any;
};

type UpdateOperation = {
    Id: string;
    AttributeName: string;
    Value: any;
};

export type SyncResult = {
    success: boolean;
    noChanges?: boolean;
    operations?: {
        create: unknown[];
        update: unknown[];
        delete: unknown[];
    };
    error?: unknown;
};

export const useSync = () => {
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const syncInFlightRef = useRef(false);

    const { useCreate, useUpdateSingleAttribute } = useInspectionAttributes();

    const { mutateAsync: createAttribute } = useCreate();
    const { mutateAsync: updateAttribute } = useUpdateSingleAttribute();
    const isOnline = useNetworkStatus();

    const syncEngine = useCallback(
        async ({
            baselineAttributes,
            setBaselineAttributes,
            currentData,
            projectId,
        }: SyncParams): Promise<SyncResult> => {
            if (syncInFlightRef.current) {
                return { success: true, noChanges: true };
            }

            syncInFlightRef.current = true;
            setSyncing(true);
            setError(null);

            const mappedId = await offlineStore.getProjectMapping(projectId);
            const realProjectId = mappedId || projectId;

            if (!isOnline || realProjectId.startsWith("offline-")) {
                try {
                    await offlineStore.saveInspection({
                        offlineId: projectId, // save using original offlineId
                        payload: currentData,
                        timestamp: Date.now(),
                        synced: false
                    });
                    return { success: true };
                } catch (err: any) {
                    setError("Failed to save offline");
                    return { success: false, error: err };
                } finally {
                    syncInFlightRef.current = false;
                    setSyncing(false);
                }
            }

            const operations = {
                create: [] as CreateOperation[],
                update: [] as UpdateOperation[],
                delete: [] as string[],
            };

            try {
                const newBaseline = [...baselineAttributes];
                const baselineMap = new Map<string, BackendAttribute>();
                baselineAttributes.forEach((attr) => {
                    baselineMap.set(attr.AttributeName, attr);
                });

                const expandedData = expandMeasurementsForSync(currentData);

                for (const key in expandedData) {
                    let value = expandedData[key];

                    if (
                        value &&
                        typeof value === "object" &&
                        !Array.isArray(value) &&
                        (value as { __measurement?: boolean }).__measurement
                    ) {
                        const measurementValue = value as { Value: string };
                        const existingAttr = baselineMap.get(key);
                        const nextValue = measurementValue.Value;

                        if (existingAttr) {
                            if (
                                String(existingAttr.AttributeValue ?? "") !==
                                String(nextValue)
                            ) {
                                operations.update.push({
                                    Id: existingAttr.Id,
                                    AttributeName: key,
                                    Value: nextValue,
                                });

                                const index = newBaseline.findIndex(
                                    (a) => a.Id === existingAttr.Id,
                                );
                                if (index !== -1) {
                                    newBaseline[index] = {
                                        ...existingAttr,
                                        AttributeValue: nextValue,
                                    };
                                }
                            }
                            baselineMap.delete(key);
                        } else if (
                            !isEmptyMeasurementAttributeValue(nextValue)
                        ) {
                            operations.create.push({
                                ProjectId: realProjectId,
                                AttributeName: key,
                                Value: nextValue,
                            });
                        }
                        continue;
                    }

                    if (
                        Array.isArray(value) ||
                        (typeof value === "object" && value !== null)
                    ) {
                        value = JSON.stringify(value);
                    }

                    if (value === undefined || value === null) {
                        value = "";
                    }

                    const existingAttr = baselineMap.get(key);

                    if (existingAttr) {
                        if (
                            String(existingAttr.AttributeValue) !==
                            String(value)
                        ) {
                            operations.update.push({
                                Id: existingAttr.Id,
                                AttributeName: key,
                                Value: value,
                            });

                            const index = newBaseline.findIndex(
                                (a) => a.Id === existingAttr.Id,
                            );
                            if (index !== -1) {
                                newBaseline[index] = {
                                    ...existingAttr,
                                    AttributeValue: value,
                                };
                            }
                        }
                        baselineMap.delete(key);
                    } else if (value !== "") {
                        operations.create.push({
                            ProjectId: realProjectId,
                            AttributeName: key,
                            Value: value,
                        });
                    }
                }

                if (
                    operations.create.length === 0 &&
                    operations.update.length === 0
                ) {
                    return { success: true, noChanges: true, operations };
                }

                for (const op of operations.update) {
                    await updateAttribute({
                        Id: op.Id,
                        Value: op.Value,
                    });
                }

                for (const op of operations.create) {
                    const result: any = await createAttribute({
                        ProjectId: op.ProjectId,
                        AttributeName: op.AttributeName,
                        Value: op.Value,
                    });

                    if (result?.Id) {
                        newBaseline.push({
                            Id: result.Id,
                            AttributeName: op.AttributeName,
                            AttributeValue: op.Value,
                        });
                    }
                }

                setBaselineAttributes(newBaseline);

                return { success: true, operations };
            } catch (err: any) {
                console.error("Sync Engine failed:", err);
                setError(err.message || "Sync failed");
                return { success: false, error: err };
            } finally {
                syncInFlightRef.current = false;
                setSyncing(false);
            }
        },
        [createAttribute, updateAttribute],
    );

    return {
        syncEngine,
        syncing,
        error,
    };
};
